<script lang="ts" setup>
import {RefugeImage} from "~/types/RefugeImage";
import InternalRedirectButton from "~/components/Buttons/InternalRedirectButton.vue";
import {refuge} from "~/data/refuge";

usePageSeo('seoHomeTitle', 'seoHomeDescription');
useHead({ titleTemplate: '%s' });

const {t} = useI18n();

// WCAG 2.2.2: content that moves for more than five seconds needs a way to
// stop it, and an OS-level "reduce motion" preference should stop it up front.
const heroVideo = ref<HTMLVideoElement | null>(null);
const videoPlaying = ref(true);

onMounted(() => {
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  heroVideo.value?.pause();
  videoPlaying.value = false;
});

function toggleHeroVideo() {
  const video = heroVideo.value;
  if (!video) return;

  if (video.paused) {
    video.play();
    videoPlaying.value = true;
  } else {
    video.pause();
    videoPlaying.value = false;
  }
}

// computed rather than ref so the alt text follows a locale switch
const stockImages = computed<RefugeImage[]>(() => [
  { link: "https://res.cloudinary.com/dlct0dnwt/image/upload/v1684618504/refuge/stock/sleep_nidfpg.jpg", alt: t('altBeds') } as RefugeImage,
  { link: "https://res.cloudinary.com/dlct0dnwt/image/upload/v1684618456/refuge/stock/food_jprilv.jpg", alt: t('altFoodOnTable') } as RefugeImage,
  { link: "https://res.cloudinary.com/dlct0dnwt/image/upload/v1684614558/refuge/stock/skier_czfpiy.jpg", alt: t('altSkier') } as RefugeImage,
])
</script>

<template>
  <div class="page-wrapper">
    <!-- Floating ESPA logo -->
    <a href="https://res.cloudinary.com/dlct0dnwt/image/upload/v1687110089/refuge/espa-tourismos_pfhs10.pdf"
       target="_blank" rel="noopener noreferrer" class="espa-link">
      <nuxt-img
          src="https://res.cloudinary.com/dlct0dnwt/image/upload/v1688320051/refuge/espa_logo_cyvem7.jpg"
          :alt="$t('espaLogoAlt')" class="espa-img"
          aspect-ratio="1/1"
          loading="lazy"
          quality="40"
      />
      <span class="sr-only">({{ $t('opensInNewTab') }})</span>
    </a>

    <!-- Hero -->
    <section class="hero">
      <div class="video-container">
        <video ref="heroVideo" autoplay muted loop playsinline class="hero-video">
          <source
              src="https://res.cloudinary.com/dlct0dnwt/video/upload/w_1920/q_auto:eco/v1707660416/refuge/videos/refuge_promotion_rvvdsg.mp4"
              type="video/mp4">
        </video>
        <div class="hero-overlay"></div>
        <button type="button" class="video-toggle"
                :aria-label="videoPlaying ? $t('pauseVideo') : $t('playVideo')"
                @click="toggleHeroVideo">
          <Icon :name="videoPlaying ? 'ph:pause-fill' : 'ph:play-fill'" size="1rem" aria-hidden="true"/>
        </button>
      </div>

      <div class="hero-content">
        <span class="hero-badge">
          <Icon name="ph:map-pin-simple" size="0.85rem"/>
          {{ $t('locationTag') }}
        </span>
        <h1 class="hero-title">{{ $t('welcomeMessage') }}</h1>
        <p class="hero-subtitle">{{ $t('refugeSubtitle') }}</p>
        <div class="hero-actions">
          <InternalRedirectButton redirect="/refuge/booking" :text="$t('Book now')" icon="ph:calendar-blank" class="hero-cta-primary"/>
          <NuxtLink to="#discover" class="hero-cta-ghost">
            {{ $t('Discover') }}
            <Icon name="ph:arrow-down" size="1rem"/>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- Specs bar -->
    <div class="specs-bar">
      <div class="spec-item">
        <Icon name="ph:mountains" size="1.6rem" class="spec-icon"/>
        <div class="spec-text">
          <span class="spec-value">{{ refuge.elevationMetres }}m</span>
          <span class="spec-label">{{ $t('altitude') }}</span>
        </div>
      </div>
      <div class="spec-divider"></div>
      <div class="spec-item">
        <Icon name="ph:users-three" size="1.6rem" class="spec-icon"/>
        <div class="spec-text">
          <span class="spec-value">{{ refuge.capacity }}</span>
          <span class="spec-label">{{ $t('capacityTag') }}</span>
        </div>
      </div>
      <div class="spec-divider"></div>
      <div class="spec-item">
        <Icon name="ph:sun-horizon" size="1.6rem" class="spec-icon"/>
        <div class="spec-text">
          <span class="spec-value">{{ $t('juneOctober') }}</span>
          <span class="spec-label">{{ $t('mainSeason') }}</span>
        </div>
      </div>
      <div class="spec-divider"></div>
      <div class="spec-item">
        <Icon name="ph:house-simple" size="1.6rem" class="spec-icon"/>
        <div class="spec-text">
          <span class="spec-value">{{ refuge.establishedYear }}</span>
          <span class="spec-label">{{ $t('builtIn') }}</span>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="main-content" id="discover" tabindex="-1">

      <section class="weather-section">
        <keep-alive>
          <weather-widget></weather-widget>
        </keep-alive>
      </section>

      <!-- Intro -->
      <section class="intro-section">
        <div class="container">
          <span class="eyebrow">{{ $t('heartOfTzoumerka') }}</span>
          <h2 class="section-title">{{ $t('launchPageHeader') }}</h2>
          <div class="text-content">
            <p>{{ $t('sectionText1') }}</p>
            <p>{{ $t('sectionText2') }}</p>
            <p>{{ $t('sectionText3') }}</p>
          </div>
        </div>
      </section>

      <!-- Destinations -->
      <section class="destinations-section">
        <div class="section-header">
          <span class="eyebrow">{{ $t('Explore') }}</span>
          <h2 class="section-title">{{ $t('allOurServices') }}</h2>
        </div>
        <div class="destinations-grid">
          <Destination :refuge-img="stockImages[0]" link="/services/accommodation" :text="$t('Accommodation')" :cta="$t('ctaSeeSleepingOptions')"/>
          <Destination :refuge-img="stockImages[1]" link="/services/menu" :text="$t('Menu')" :cta="$t('ctaSeeTheMenu')"/>
          <Destination :refuge-img="stockImages[2]" link="/activities" :text="$t('Activities')" :cta="$t('ctaSeeTheActivities')"/>
        </div>
      </section>

      <!-- Info highlight -->
      <section class="info-highlight-section">
        <div class="info-container">
          <div class="info-image-wrapper">
            <nuxt-img
                src="https://res.cloudinary.com/dlct0dnwt/image/upload/v1684614474/refuge/outside/main_yjac0p.jpg"
                :alt="$t('altRefugeExterior')"
                format="webp" loading="lazy"
                class="info-img"/>
          </div>

          <div class="info-text-wrapper">
            <span class="eyebrow eyebrow--dark">{{ $t('The Refuge') }}</span>
            <h2 class="info-title">{{ $t('informationText') }}</h2>
            <div class="stats-row">
              <div class="stat-item">
                <span class="stat-value">1998</span>
                <span class="stat-label">{{ $t('builtIn') }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">2022</span>
                <span class="stat-label">{{ $t('renovatedIn') }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">1665m</span>
                <span class="stat-label">{{ $t('altitude') }}</span>
              </div>
            </div>

          </div>
        </div>

        <InternalRedirectButton redirect="/refuge/booking" :text="$t('bookingInformation')" class="info-btn"
                                icon="ph:calendar-blank"/>
      </section>

    </div>
  </div>
</template>

<style lang="sass" scoped>
.page-wrapper
  width: 100vw
  overflow-x: hidden

// ─── Hero ────────────────────────────────────────────────────────────────────

.hero
  height: 100vh
  min-height: 600px
  width: 100vw
  position: relative
  display: flex
  align-items: center
  justify-content: center
  background-color: #000

.video-container
  position: absolute
  inset: 0
  overflow: hidden

.hero-video
  width: 100%
  height: 100%
  object-fit: cover

.hero-overlay
  position: absolute
  inset: 0
  background: linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.65) 100%)

.hero-content
  position: relative
  z-index: 10
  text-align: center
  color: white
  display: flex
  flex-direction: column
  align-items: center
  gap: 1.5rem
  padding: 0 2rem
  max-width: 900px

.hero-badge
  display: inline-flex
  align-items: center
  gap: 0.4rem
  font-size: 0.75rem
  font-weight: 700
  letter-spacing: 3px
  text-transform: uppercase
  background: rgba(255, 255, 255, 0.12)
  border: 1px solid rgba(255, 255, 255, 0.3)
  backdrop-filter: blur(8px)
  padding: 0.45rem 1.1rem
  border-radius: 100px
  color: rgba(255,255,255,0.95)

.hero-title
  font-size: 3rem
  font-weight: 800
  letter-spacing: -1.5px
  line-height: 1.1
  text-shadow: 0 4px 24px rgba(0,0,0,0.4)
  margin: 0

.hero-subtitle
  font-size: clamp(1rem, 2.5vw, 1.35rem)
  font-weight: 400
  opacity: 0.88
  margin: 0
  max-width: 620px
  line-height: 1.6
  text-shadow: 0 2px 12px rgba(0,0,0,0.3)

.hero-actions
  display: flex
  align-items: center
  gap: 1.25rem
  margin-top: 0.5rem
  flex-wrap: wrap
  justify-content: center

  :deep(.refuge-btn)
    border-color: white
    color: white
    background: rgba(255,255,255,0.08)
    backdrop-filter: blur(8px)

    &:hover
      background-color: white
      color: $color-dark-bg
      border-color: white

.hero-cta-ghost
  display: inline-flex
  align-items: center
  gap: 0.5rem
  font-size: 0.9rem
  font-weight: 700
  letter-spacing: 1.5px
  text-transform: uppercase
  color: white
  text-decoration: none
  padding: 0.75rem 1.5rem
  border: 2px solid rgba(255,255,255,0.5)
  border-radius: $border-radius-btn
  transition: all $transition-standard
  backdrop-filter: blur(4px)

  &:hover
    background: rgba(255,255,255,0.15)
    border-color: rgba(255,255,255,0.9)

.video-toggle
  position: absolute
  bottom: 1.5rem
  left: 1.5rem
  z-index: 15
  display: flex
  align-items: center
  justify-content: center
  width: 2.25rem
  height: 2.25rem
  padding: 0
  border-radius: 50%
  border: 1px solid rgba(255, 255, 255, 0.3)
  background: rgba(255, 255, 255, 0.12)
  backdrop-filter: blur(8px)
  color: rgba(255, 255, 255, 0.85)
  cursor: pointer
  opacity: 0.55
  transition: all $transition-standard

  &:hover, &:focus-visible
    opacity: 1
    background: rgba(255, 255, 255, 0.22)

// ─── Specs Bar ───────────────────────────────────────────────────────────────

.specs-bar
  position: relative
  z-index: 20
  background: white
  display: flex
  align-items: center
  justify-content: center
  gap: 0
  padding: 2rem 3rem
  box-shadow: 0 4px 32px rgba(0,0,0,0.08)
  flex-wrap: wrap

.dark-mode .specs-bar
  background: $color-surface-dark

.spec-item
  display: flex
  align-items: center
  gap: 1rem
  padding: 0.5rem 3rem

.spec-divider
  width: 1px
  height: 2.5rem
  background: rgba(0,0,0,0.1)
  flex-shrink: 0

.dark-mode .spec-divider
  background: rgba(255,255,255,0.1)

.spec-icon
  color: $color-primary
  flex-shrink: 0

.spec-text
  display: flex
  flex-direction: column
  gap: 0.1rem

.spec-value
  font-size: 1.2rem
  font-weight: 800
  line-height: 1

.spec-label
  font-size: 0.7rem
  text-transform: uppercase
  letter-spacing: 1.5px
  font-weight: 600
  opacity: 0.5

// ─── Main Content ────────────────────────────────────────────────────────────

.main-content
  background-color: white
  position: relative
  z-index: 10

.dark-mode .main-content
  background-color: $color-dark-bg

.weather-section
  padding: 4rem 2rem 0.75rem
  display: flex
  justify-content: center
  width: 100%

// ─── Eyebrow ─────────────────────────────────────────────────────────────────

.eyebrow
  display: block
  font-size: 0.72rem
  font-weight: 800
  letter-spacing: 3px
  text-transform: uppercase
  color: $color-primary
  margin-bottom: 1rem

.eyebrow--dark
  color: $color-primary

// ─── Intro Section ───────────────────────────────────────────────────────────

.intro-section
  padding: 3.5rem 0 6rem

.container
  max-width: 860px
  margin: 0 auto
  padding: 0 2rem
  text-align: center

.section-title
  font-size: clamp(1.6rem, 3vw, 2.4rem)
  font-weight: 800
  line-height: 1.25
  margin: 0 0 2.5rem
  letter-spacing: -0.5px

.text-content
  text-align: left
  line-height: 1.85
  font-size: 1.1rem
  color: rgba(0, 0, 0, 0.65)

  p
    margin-bottom: 1.4rem

    &:last-child
      margin-bottom: 0

.dark-mode .text-content
  color: rgba(255, 255, 255, 0.65)

// ─── Destinations Section ────────────────────────────────────────────────────

.destinations-section
  padding: 2rem 2rem 7rem

.section-header
  text-align: center
  margin-bottom: 3.5rem

  .section-title
    margin-bottom: 0

.destinations-grid
  display: grid
  grid-template-columns: repeat(3, 1fr)
  gap: 2.5rem
  max-width: 1200px
  margin: 0 auto

// ─── Info Highlight Section ──────────────────────────────────────────────────

.info-highlight-section
  padding: 5rem 0
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center
  gap: 2rem

.info-container
  max-width: 1300px
  margin: 0 auto
  display: grid
  grid-template-columns: 1.4fr 1fr
  gap: 4rem
  align-items: center
  padding: 0 3rem

.info-image-wrapper
  border-radius: 16px
  overflow: hidden
  box-shadow: $shadow-modern
  aspect-ratio: 16/9

.info-img
  width: 100%
  height: 100%
  object-fit: cover

.info-text-wrapper
  display: flex
  flex-direction: column
  gap: 1.25rem

.info-title
  font-size: clamp(1.1rem, 1.6vw, 1.35rem)
  font-weight: 600
  margin: 0
  line-height: 1.6
  opacity: 0.8

.stats-row
  display: flex
  gap: 2.5rem
  padding: 1rem 0
  border-top: 1px solid rgba(0,0,0,0.08)
  border-bottom: 1px solid rgba(0,0,0,0.08)

.dark-mode .stats-row
  border-color: rgba(255,255,255,0.08)

.stat-item
  display: flex
  flex-direction: column
  gap: 0.25rem

.stat-value
  font-size: 1.75rem
  font-weight: 800
  color: $color-primary
  line-height: 1

.stat-label
  font-size: 0.7rem
  text-transform: uppercase
  letter-spacing: 1.5px
  font-weight: 700
  opacity: 0.45

// ─── ESPA ────────────────────────────────────────────────────────────────────

.espa-link
  position: fixed
  bottom: 2rem
  right: 2rem
  z-index: 50
  transition: transform $transition-standard

  &:hover
    transform: scale(1.05)

.espa-img
  width: 100px
  border-radius: 6px
  box-shadow: $shadow-modern

// ─── Responsive ──────────────────────────────────────────────────────────────

@media (max-width: 1024px)
  .spec-item
    padding: 0.5rem 1.75rem

@media (max-width: 768px)
  .hero
    height: 100svh

  .hero-title
    font-size: 2rem

  .specs-bar
    display: grid
    grid-template-columns: 1fr 1fr
    gap: 0
    padding: 1.25rem 1.5rem

  .spec-item
    padding: 0.75rem 1rem
    justify-content: center
    gap: 0.6rem

  .spec-divider
    display: none

  .destinations-grid
    grid-template-columns: 1fr
    max-width: 420px

  .info-container
    grid-template-columns: 1fr
    gap: 2rem
    padding: 0 1.25rem

  .info-image-wrapper
    width: 100%

  .info-text-wrapper
    padding: 0

  .intro-section
    padding: 4rem 0

  .espa-img
    width: 70px

  .stats-row
    gap: 1.5rem
</style>
